import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Papa from 'papaparse';
import { FiDownload, FiPlusCircle, FiSearch, FiTrash2, FiUpload } from 'react-icons/fi';
import inventoryService from '../../services/inventoryService';
import { formatInStoreTimezone, toApiDateValue } from '../../utils/dateUtils';

const normalizeNumber = (value) => {
  if (value == null) return NaN;
  if (typeof value === 'number') return value;
  const cleaned = String(value)
    .replace(/[^0-9.+-]/g, '')
    .replace(/^\.+/, '');
  if (!cleaned || cleaned === '.' || cleaned === '+' || cleaned === '-' || cleaned === '+.' || cleaned === '-.') {
    return NaN;
  }
  return Number(cleaned);
};

const parseBoolean = (value) => {
  if (value === true || value === false) return value;
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n'].includes(normalized)) return false;
  return undefined;
};

const numberOrUndefined = (value) => {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }
  const parsed = normalizeNumber(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeBarcodeValue = (value) => (typeof value === 'string' ? value.trim() : '');

const blankExistingLine = (product = null) => {
  return {
    mode: 'existing',
    product: product?._id || '',
    quantity: 1,
    unitCost: 0,
    gstApplicable: product?.gstApplicable !== false,
    price: '',
    supplierSku: '',
    pricingInfo: null,
  };
};

const blankNewLine = () => ({
  mode: 'new',
  name: '',
  sku: '',
  barcode: '',
  category: '',
  subcategory: '',
  gstApplicable: true,
  price: '',
  costPrice: '',
  quantity: 1,
  unitCost: 0,
  supplierSku: '',
  pricingInfo: null,
});

const DEFAULT_TAX_RATE = 10;
const LINES_PER_PAGE = 10;
const DELTA_THRESHOLD = 0.01;
const PRODUCT_SEARCH_DEBOUNCE_MS = 250;
let nextClientLineId = 0;

const createClientLineId = () => {
  nextClientLineId += 1;
  return `po-line-${Date.now()}-${nextClientLineId}`;
};

const formatProductSubtitle = (product) => {
  if (!product) return '';
  const bits = [
    product.sku ? `SKU: ${product.sku}` : null,
    product.barcode ? `Barcode: ${product.barcode}` : null,
  ].filter(Boolean);
  return bits.join(' • ');
};

const formatApproverName = (approvedBy) => {
  if (!approvedBy) return '—';
  if (typeof approvedBy === 'string') return approvedBy;
  return approvedBy.name || approvedBy.userId || approvedBy.email || '—';
};

const formatApprovalDate = (approvedDate, timezone) => {
  if (!approvedDate) return '—';
  return formatInStoreTimezone(approvedDate, 'dd-MM-yyyy HH:mm', timezone) || '—';
};

const PurchaseOrderForm = ({
  purchaseOrder,
  suppliers,
  products,
  defaultStoreId,
  defaultStoreName,
  timezone = 'Australia/Melbourne',
  onSave,
  onCancel,
  saving = false,
  readOnly = false,
}) => {
  const [form, setForm] = useState({
    supplier: '',
    destinationStore: defaultStoreId || '',
    expectedDate: '',
    invoiceNumber: '',
    invoiceDate: '',
    deliveryDate: '',
    notes: '',
    orderValue: '',
    amount: '',
    totalAmount: '',
    supplierInvoiceAmount: '',
    invoiceTaxRate: DEFAULT_TAX_RATE,
    lines: [],
  });
  const [linePage, setLinePage] = useState(1);
  const [lineFilter, setLineFilter] = useState('all');
  const [lineSearch, setLineSearch] = useState('');
  const [productCache, setProductCache] = useState(() => (Array.isArray(products) ? products : []));
  const [catalogLookup, setCatalogLookup] = useState({
    lineIndex: null,
    query: '',
    results: [],
    loading: false,
    error: '',
  });
  const isReadOnly = !!readOnly;

  const rememberProducts = useCallback((incomingProducts = []) => {
    if (!Array.isArray(incomingProducts) || incomingProducts.length === 0) return;
    setProductCache((prev) => {
      const merged = new Map();
      [...prev, ...incomingProducts].forEach((product) => {
        if (product?._id) {
          merged.set(String(product._id), product);
        }
      });
      return Array.from(merged.values());
    });
  }, []);

  useEffect(() => {
    rememberProducts(Array.isArray(products) ? products : []);
  }, [products, rememberProducts]);

  const productMetaById = useMemo(() => {
    const map = new Map();
    (productCache || []).forEach((product) => {
      if (product?._id) {
        map.set(String(product._id), product);
      }
    });
    return map;
  }, [productCache]);

  const getProductMeta = useCallback(
    (productId) => {
      if (!productId) return undefined;
      return productMetaById.get(String(productId));
    },
    [productMetaById]
  );

  const productSkuMap = useMemo(() => {
    const map = new Map();
    productMetaById.forEach((product) => {
      if (typeof product?.sku === 'string' && product.sku.trim()) {
        map.set(product.sku.trim().toLowerCase(), product);
      }
    });
    return map;
  }, [productMetaById]);

  const productBarcodeMap = useMemo(() => {
    const map = new Map();
    productMetaById.forEach((product) => {
      if (typeof product?.barcode === 'string' && product.barcode.trim()) {
        map.set(product.barcode.trim(), product);
      }
    });
    return map;
  }, [productMetaById]);

  const resolveProductForLine = useCallback(
    (line) => {
      if (!line) return null;
      if (line.mode === 'existing') {
        return (
          getProductMeta(line.product) || {
            _id: line.product,
            name: line.name || line.description || 'PO Item',
            sku: line.sku || '',
            barcode: line.barcode || '',
            price: numberOrUndefined(line.price),
            costPrice: numberOrUndefined(line.costPrice ?? line.unitCost),
            gstApplicable: line.gstApplicable !== false,
          }
        );
      }
      const skuKey = typeof line.sku === 'string' ? line.sku.trim().toLowerCase() : '';
      if (skuKey && productSkuMap.has(skuKey)) {
        return productSkuMap.get(skuKey);
      }
      const barcodeKey = typeof line.barcode === 'string' ? line.barcode.trim() : '';
      if (barcodeKey && productBarcodeMap.has(barcodeKey)) {
        return productBarcodeMap.get(barcodeKey);
      }
      return null;
    },
    [getProductMeta, productSkuMap, productBarcodeMap]
  );

  useEffect(() => {
    if (purchaseOrder) {
      rememberProducts(
        (purchaseOrder.lines || [])
          .map((line) => line.product)
          .filter((product) => product && typeof product === 'object')
      );
      setForm({
        supplier: purchaseOrder.supplier?._id || purchaseOrder.supplier || '',
        destinationStore: purchaseOrder.destinationStore?._id || purchaseOrder.destinationStore || defaultStoreId,
        expectedDate: toApiDateValue(purchaseOrder.expectedDate),
        invoiceNumber: purchaseOrder.invoiceNumber || '',
        invoiceDate: toApiDateValue(purchaseOrder.invoiceDate),
        deliveryDate: toApiDateValue(purchaseOrder.deliveryDate),
        notes: purchaseOrder.notes || '',
        orderValue: purchaseOrder.orderValue ?? purchaseOrder.amount ?? '',
        amount: purchaseOrder.amount ?? '',
        totalAmount: purchaseOrder.totalAmount ?? purchaseOrder.amount ?? '',
        supplierInvoiceAmount: purchaseOrder.supplierInvoiceAmount ?? '',
        invoiceTaxRate: purchaseOrder.invoiceTaxRate ?? DEFAULT_TAX_RATE,
        lines:
          purchaseOrder.lines?.map((line, index) => {
            const productId = line.product?._id || line.product;
            const meta = line.product && typeof line.product === 'object' ? line.product : undefined;
            const baselinePrice = line.pricingInfo?.baselinePrice ?? meta?.price;
            const baselineCost = line.pricingInfo?.baselineCost ?? meta?.costPrice ?? line.unitCost;
            return {
              clientLineId: line._id || `${productId || 'existing'}-${index}`,
              mode: 'existing',
              product: productId,
              quantity: line.quantity,
              unitCost: line.unitCost,
              supplierSku: line.supplierSku || '',
              name: line.product?.name || line.description || meta?.name,
              description: line.description,
              sku: line.sku || meta?.sku || '',
              barcode: line.barcode ?? meta?.barcode ?? '',
              price: line.price != null ? line.price : baselinePrice ?? '',
              costPrice: line.costPrice ?? baselineCost ?? '',
              category: line.category || '',
              subcategory: line.subcategory || '',
              gstApplicable: line.gstApplicable !== undefined ? line.gstApplicable : true,
              pricingInfo: line.pricingInfo || null,
            };
          }) || [{ ...blankExistingLine(), clientLineId: createClientLineId() }],
      });
      setLinePage(1);
      setLineFilter('all');
      setLineSearch('');
    } else {
      setForm((prev) => ({
        ...prev,
        lines: [],
        orderValue: '',
        invoiceTaxRate: DEFAULT_TAX_RATE,
        supplierInvoiceAmount: '',
      }));
      setLinePage(1);
      setLineFilter('all');
      setLineSearch('');
    }
    setCatalogLookup({
      lineIndex: null,
      query: '',
      results: [],
      loading: false,
      error: '',
    });
  }, [purchaseOrder, defaultStoreId, rememberProducts]);

  const handleChange = (event) => {
    if (isReadOnly) return;
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const updateLine = (index, field, value) => {
    if (isReadOnly) return;
    setForm((prev) => {
      const updated = prev.lines.map((line, idx) => {
        if (idx !== index) return line;
        if (field === 'product') {
          const meta = getProductMeta(value);
          return {
            ...line,
            product: value,
            sku: meta?.sku || '',
            barcode: meta?.barcode || '',
            gstApplicable: meta?.gstApplicable !== false,
            pricingInfo: null,
          };
        }
        return { ...line, [field]: value };
      });
      return { ...prev, lines: updated };
    });
  };

  const closeCatalogLookup = useCallback(() => {
    setCatalogLookup({
      lineIndex: null,
      query: '',
      results: [],
      loading: false,
      error: '',
    });
  }, []);

  const openCatalogLookup = useCallback(
    (index) => {
      const line = form.lines[index];
      const selectedProduct = getProductMeta(line?.product);
      setCatalogLookup({
        lineIndex: index,
        query: '',
        results: selectedProduct ? [selectedProduct] : [],
        loading: false,
        error: '',
      });
    },
    [form.lines, getProductMeta]
  );

  const selectCatalogProduct = useCallback(
    (index, product) => {
      rememberProducts(product ? [product] : []);
      setForm((prev) => ({
        ...prev,
        lines: prev.lines.map((line, idx) => {
          if (idx !== index) return line;
          return {
            ...line,
            product: product?._id || '',
            name: product?.name || line.name || '',
            description: line.description || product?.name || '',
            sku: product?.sku || '',
            barcode: product?.barcode || '',
            category: product?.category || line.category || '',
            subcategory: product?.subcategory || line.subcategory || '',
            gstApplicable: product?.gstApplicable !== false,
            price:
              line.price !== '' && line.price !== undefined && line.price !== null
                ? line.price
                : product?.price ?? '',
            costPrice:
              line.costPrice !== '' && line.costPrice !== undefined && line.costPrice !== null
                ? line.costPrice
                : product?.costPrice ?? '',
            pricingInfo: null,
          };
        }),
      }));
      closeCatalogLookup();
    },
    [closeCatalogLookup, rememberProducts]
  );

  useEffect(() => {
    if (catalogLookup.lineIndex === null || !defaultStoreId) {
      return undefined;
    }

    const query = catalogLookup.query.trim();
    const activeLine = form.lines[catalogLookup.lineIndex];
    const selectedProduct = getProductMeta(activeLine?.product);

    if (!query) {
      setCatalogLookup((prev) =>
        prev.lineIndex === null
          ? prev
          : {
              ...prev,
              results: selectedProduct ? [selectedProduct] : [],
              loading: false,
              error: '',
            }
      );
      return undefined;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setCatalogLookup((prev) => ({ ...prev, loading: true, error: '' }));
        const results = await inventoryService.searchProducts(defaultStoreId, query, {
          lookupContext: 'purchase-order',
        });
        const nextResults = Array.isArray(results) ? results : [];
        rememberProducts(nextResults);
        setCatalogLookup((prev) => {
          if (prev.lineIndex !== catalogLookup.lineIndex || prev.query.trim() !== query) {
            return prev;
          }
          return {
            ...prev,
            results: nextResults,
            loading: false,
            error: '',
          };
        });
      } catch (err) {
        setCatalogLookup((prev) => {
          if (prev.lineIndex !== catalogLookup.lineIndex || prev.query.trim() !== query) {
            return prev;
          }
          return {
            ...prev,
            results: [],
            loading: false,
            error: err.response?.data?.message || 'Unable to search products right now.',
          };
        });
      }
    }, PRODUCT_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [catalogLookup.lineIndex, catalogLookup.query, defaultStoreId, form.lines, getProductMeta, rememberProducts]);

  const switchLineMode = (index, mode) => {
    if (isReadOnly) return;
    setForm((prev) => {
      const next = prev.lines.map((line, idx) => {
        if (idx !== index) return line;
        if (mode === 'new') {
          return {
            ...blankNewLine(),
            clientLineId: line.clientLineId || createClientLineId(),
            supplierSku: line.supplierSku,
          };
        }
        return {
          ...blankExistingLine(),
          clientLineId: line.clientLineId || createClientLineId(),
          supplierSku: line.supplierSku,
        };
      });
      return { ...prev, lines: next };
    });
    closeCatalogLookup();
  };

  const removeLine = (index) => {
    if (isReadOnly) return;
    setForm((prev) => ({ ...prev, lines: prev.lines.filter((_, idx) => idx !== index) }));
    closeCatalogLookup();
  };

  const addLine = () => {
    if (isReadOnly) return;
    const nextPage = Math.max(1, Math.ceil((form.lines.length + 1) / LINES_PER_PAGE));
    setForm((prev) => ({
      ...prev,
      lines: [...prev.lines, { ...blankExistingLine(), clientLineId: createClientLineId() }],
    }));
    setLineFilter('all');
    setLineSearch('');
    setLinePage(nextPage);
  };

  const buildLookup = (row) => {
    const lookup = {};
    Object.entries(row).forEach(([key, value]) => {
      if (!key) return;
      lookup[key] = value;
      lookup[key.trim().toLowerCase()] = value;
    });
    return lookup;
  };

  const pickValue = (lookup, keys = []) => {
    for (const key of keys) {
      if (!key) continue;
      const normalizedKey = typeof key === 'string' ? key.trim().toLowerCase() : key;
      const candidates = [key, normalizedKey];
      for (const candidate of candidates) {
        if (candidate == null) continue;
        const val = lookup[candidate];
        if (val !== undefined && val !== null && `${val}`.trim() !== '') {
          return typeof val === 'string' ? val.trim() : val;
        }
      }
    }
    return '';
  };

  const pickNumber = (lookup, keys, fallback = 0) => {
    const raw = pickValue(lookup, keys);
    const value = normalizeNumber(raw);
    return Number.isFinite(value) ? value : fallback;
  };

  const hasMeaningfulDelta = (value) => Number.isFinite(value) && Math.abs(value) >= DELTA_THRESHOLD;

  const formatCurrency = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '—';
    return numeric.toFixed(2);
  };

  const formatDelta = (value) => {
    if (!Number.isFinite(value) || !hasMeaningfulDelta(value)) return null;
    const abs = Math.abs(value).toFixed(2);
    return `${value >= 0 ? '+' : '-'}$${abs}`;
  };

  const getLineTotal = (line) => {
    const qty = numberOrUndefined(line.quantity) ?? 0;
    const cost = numberOrUndefined(line.unitCost) ?? 0;
    return qty * cost;
  };

  const calculateInvoiceSummary = (lines, invoiceTaxRateValue) => {
    const lineTotals = lines.map((line) => getLineTotal(line));
    const subtotal = lineTotals.reduce((sum, val) => sum + val, 0);
    const taxableSubtotal = lines.reduce((sum, line, index) => {
      const inferredGstApplicable =
        line?.gstApplicable !== undefined && line?.gstApplicable !== null
          ? line.gstApplicable !== false
          : resolveProductForLine(line)?.gstApplicable !== false;
      if (!inferredGstApplicable) return sum;
      return sum + (lineTotals[index] || 0);
    }, 0);
    const rate = Number.isFinite(invoiceTaxRateValue) ? invoiceTaxRateValue : DEFAULT_TAX_RATE;
    const taxAmount = taxableSubtotal * (rate / 100);
    return {
      lineTotals,
      subtotal,
      taxAmount,
      rate,
      grandTotal: subtotal + taxAmount,
    };
  };

  const BARCODE_REGEX = /^[0-9]+$/;

  const getBarcodeMismatchInfo = useCallback(
    (line, resolvedProduct = null) => {
      const submittedBarcode = normalizeBarcodeValue(line?.barcode);
      const product = resolvedProduct || resolveProductForLine(line);
      const canonicalProductBarcode = normalizeBarcodeValue(product?.barcode);
      const hasMismatch =
        line?.mode === 'existing' &&
        submittedBarcode &&
        canonicalProductBarcode &&
        submittedBarcode !== canonicalProductBarcode;
      return {
        hasMismatch,
        submittedBarcode,
        canonicalProductBarcode,
      };
    },
    [resolveProductForLine]
  );

  const findBarcodeValidationError = () => {
    const barcodeToProducts = new Map();

    for (const line of form.lines) {
      const resolvedProduct = resolveProductForLine(line);
      const { canonicalProductBarcode, submittedBarcode } = getBarcodeMismatchInfo(line, resolvedProduct);
      const effectiveBarcode =
        line.mode === 'existing'
          ? canonicalProductBarcode || submittedBarcode
          : submittedBarcode;
      if (!effectiveBarcode) continue;

      const resolvedProductId = resolvedProduct?._id ? String(resolvedProduct._id) : '';
      const normalizedSku = typeof line?.sku === 'string' ? line.sku.trim().toLowerCase() : '';
      const normalizedName = typeof line?.name === 'string' ? line.name.trim().toLowerCase() : '';
      const productIdentity =
        resolvedProductId ||
        (normalizedSku ? `sku:${normalizedSku}` : normalizedName ? `name:${normalizedName}` : `line`);
      if (!barcodeToProducts.has(effectiveBarcode)) {
        barcodeToProducts.set(effectiveBarcode, { identities: new Set() });
      }
      const entry = barcodeToProducts.get(effectiveBarcode);
      entry.identities.add(productIdentity);
    }

    const duplicate = [...barcodeToProducts.entries()].find(([, meta]) => meta.identities.size > 1);
    if (!duplicate) {
      return null;
    }
    const [barcode] = duplicate;
    return `Barcode ${barcode} is used for different products in this purchase order.`;
  };

  const handleImportCsv = (event) => {
    if (isReadOnly) return;
    const file = event.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const newLines = [];
        for (let index = 0; index < results.data.length; index += 1) {
          const row = results.data[index];
          const lookup = buildLookup(row);
          const name =
            pickValue(lookup, ['product name', 'product', 'item', 'name', 'description', 'item name']) ||
            `Imported item ${index + 1}`;
          const sku = pickValue(lookup, ['sku', 'item code', 'product code', 'code']);
          const supplierSku = pickValue(lookup, ['supplier sku', 'supplier code', 'vendor sku', 'supplier_sku']) || sku;
          const rawBarcode = pickValue(lookup, ['barcode', 'ean', 'ean13', 'upc']) || '';
          const normalizedBarcode = typeof rawBarcode === 'string' ? rawBarcode.trim() : '';
          if (normalizedBarcode && !BARCODE_REGEX.test(normalizedBarcode)) {
            alert(
              `Invalid barcode detected on imported row ${index + 2}. Barcodes must be blank or numeric.`
            );
            return;
          }
          const category = pickValue(lookup, ['category', 'category name', 'department']);
          const subcategory = pickValue(lookup, ['sub category', 'subcategory', 'sub-category']);
          const gstApplicable = parseBoolean(
            pickValue(lookup, ['gst applicable', 'gst', 'gst applicable (true/false)'])
          );
          newLines.push({
            mode: 'new',
            name,
            description: name,
            sku,
            barcode: normalizedBarcode,
            category,
            subcategory,
            gstApplicable,
            price: pickNumber(
              lookup,
              [
                'price',
                'retail',
                'retail price',
                'retail inc gst',
                'sell price',
                'selling price',
                'selling price inc gst',
                'price inc gst',
              ],
              undefined
            ),
            costPrice: pickNumber(lookup, ['cost price', 'costprice', 'cost', 'buy price', 'landed cost'], undefined),
            quantity: pickNumber(
              lookup,
              [
                'stock',
                'stock quantity',
                'qty',
                'quantity',
                'order quantity',
                'qty ordered',
                'qty order',
                'quantity ordered',
                'ordered qty',
                'ordered quantity',
                'qtyordered',
                'qty ordered units',
                'qty (units)',
                'order qty',
                'qty to order',
                'qty_to_order',
                'qty__ordered',
              ],
              0
            ),
            unitCost: pickNumber(lookup, ['unit cost', 'unitcost', 'cost', 'cost price', 'buy price'], undefined),
            supplierSku,
            pricingInfo: null,
          });
        }
        if (!newLines.length) return;
        setForm((prev) => ({ ...prev, lines: [...prev.lines, ...newLines] }));
        setLinePage(1);
      },
      error: () => {
        alert(
          'Unable to parse CSV. Expected headers include Product Name, SKU, Barcode, Retail Price, Cost Price, Stock Quantity, Description, GST Applicable.'
        );
      },
    });
  };

  const handleDownloadTemplate = () => {
    const headers = [
      'Product Name',
      'SKU',
      'Barcode',
      'Retail Price',
      'Cost Price',
      'Stock Quantity',
      'Description',
      'GST Applicable (true/false)',
    ];
    const csvContent = `data:text/csv;charset=utf-8,${headers.join(',')}`;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'purchase_order_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isReadOnly) {
      onCancel();
      return;
    }
    for (const [index, line] of form.lines.entries()) {
      if (line.mode === 'existing' && !line.product) {
        alert(`Line ${index + 1} needs a catalog product selected before saving.`);
        return;
      }
      const normalizedBarcode = typeof line.barcode === 'string' ? line.barcode.trim() : '';
      if (normalizedBarcode && !BARCODE_REGEX.test(normalizedBarcode)) {
        alert(
          `Line ${index + 1} has an invalid barcode (${normalizedBarcode}). Barcodes must be blank or numeric.`
        );
        return;
      }
    }
    const barcodeValidationError = findBarcodeValidationError();
    if (barcodeValidationError) {
      alert(barcodeValidationError);
      return;
    }
    const payloadLines = form.lines.map((line) => {
      const { clientLineId, pricingInfo, ...rest } = line;
      const resolvedProduct = resolveProductForLine(line);
      const resolvedProductBarcode = normalizeBarcodeValue(resolvedProduct?.barcode);
      const submittedBarcode = normalizeBarcodeValue(line.barcode);
      const finalBarcode =
        line.mode === 'existing'
          ? resolvedProductBarcode || submittedBarcode || undefined
          : submittedBarcode || undefined;
      return {
        ...rest,
        quantity: numberOrUndefined(line.quantity) ?? 0,
        unitCost: numberOrUndefined(line.unitCost) ?? 0,
        barcode: finalBarcode,
        price: numberOrUndefined(line.price),
        costPrice: numberOrUndefined(line.costPrice),
        description: line.description || line.name || undefined,
        gstApplicable:
          line.gstApplicable === undefined || line.gstApplicable === null
            ? undefined
            : Boolean(line.gstApplicable),
      };
    });
    const invoiceTaxRateValue = numberOrUndefined(form.invoiceTaxRate);
    const { grandTotal, rate } = calculateInvoiceSummary(payloadLines, invoiceTaxRateValue);
    const orderValue = numberOrUndefined(form.orderValue);
    const effectiveTotal = payloadLines.length ? grandTotal : orderValue ?? 0;
    onSave({
      ...form,
      lines: payloadLines,
      invoiceTaxRate: rate,
      orderValue,
      amount: effectiveTotal,
      totalAmount: effectiveTotal,
      supplierInvoiceAmount: numberOrUndefined(form.supplierInvoiceAmount),
    });
  };

  const invoiceSummary = calculateInvoiceSummary(form.lines, numberOrUndefined(form.invoiceTaxRate));
  const { lineTotals, subtotal: invoiceSubtotal, taxAmount: invoiceTaxAmount, grandTotal: computedInvoiceAmount } = invoiceSummary;
  const displaySummary = purchaseOrder
    ? {
        subtotal: purchaseOrder.invoiceSubtotal ?? invoiceSubtotal,
        tax: purchaseOrder.invoiceTaxAmount ?? invoiceTaxAmount,
        grandTotal:
          purchaseOrder.totalAmount ??
          purchaseOrder.amount ??
          purchaseOrder.totals?.grandTotal ??
          computedInvoiceAmount,
        lineCount: purchaseOrder.lines?.length ?? form.lines.length,
        taxRate: purchaseOrder.invoiceTaxRate ?? form.invoiceTaxRate ?? DEFAULT_TAX_RATE,
      }
    : {
        subtotal: invoiceSubtotal,
        tax: invoiceTaxAmount,
        grandTotal: computedInvoiceAmount,
        lineCount: form.lines.length,
        taxRate: numberOrUndefined(form.invoiceTaxRate) ?? DEFAULT_TAX_RATE,
      };
  const approvalDateDisplay = formatApprovalDate(purchaseOrder?.approvedDate, timezone);
  const approverNameDisplay = formatApproverName(purchaseOrder?.approvedBy);

  const lineMetaEntries = useMemo(
    () =>
      form.lines.map((line, index) => {
        const info = line.pricingInfo || {};
        const productMeta = resolveProductForLine(line);
        const baselineCost = numberOrUndefined(info.baselineCost ?? productMeta?.costPrice);
        const baselinePrice = numberOrUndefined(info.baselinePrice ?? productMeta?.price);
        const currentCost = numberOrUndefined(line.unitCost);
        const currentPrice = numberOrUndefined(line.price);

        const fallbackCostDelta = Number(info.costDelta);
        const fallbackPriceDelta = Number(info.priceDelta);

        const derivedCostDelta =
          baselineCost !== undefined && currentCost !== undefined
            ? currentCost - baselineCost
            : Number.isFinite(fallbackCostDelta)
            ? fallbackCostDelta
            : undefined;
        const derivedPriceDelta =
          baselinePrice !== undefined && currentPrice !== undefined
            ? currentPrice - baselinePrice
            : Number.isFinite(fallbackPriceDelta)
            ? fallbackPriceDelta
            : undefined;

        const initialCostFlag = info.hasCostDelta === true;
        const initialPriceFlag = info.hasPriceDelta === true;
        const hasCostDelta = hasMeaningfulDelta(derivedCostDelta) || initialCostFlag;
        const hasPriceDelta = hasMeaningfulDelta(derivedPriceDelta) || initialPriceFlag;
        const { hasMismatch, submittedBarcode, canonicalProductBarcode } = getBarcodeMismatchInfo(line, productMeta);

        return {
          line,
          index,
          baselineCost,
          baselinePrice,
          costDelta: derivedCostDelta,
          priceDelta: derivedPriceDelta,
          hasCostDelta,
          hasPriceDelta,
          hasBarcodeMismatch: hasMismatch,
          lineBarcodeInput: submittedBarcode,
          productBarcode: canonicalProductBarcode,
        };
      }),
    [form.lines, resolveProductForLine, getBarcodeMismatchInfo]
  );

  const filteredLineEntries = useMemo(() => {
    let entries = lineMetaEntries;
    if (lineFilter === 'cost') {
      entries = entries.filter((entry) => entry.hasCostDelta);
    } else if (lineFilter === 'price') {
      entries = entries.filter((entry) => entry.hasPriceDelta);
    } else if (lineFilter === 'any') {
      entries = entries.filter(
        (entry) => entry.hasCostDelta || entry.hasPriceDelta || entry.hasBarcodeMismatch
      );
    }

    const searchValue = lineSearch.trim().toLowerCase();
    if (searchValue) {
      entries = entries.filter(({ line }) => {
        const productMeta = resolveProductForLine(line);
        const haystack = [
          line.name,
          line.description,
          line.sku,
          line.barcode,
          line.supplierSku,
          productMeta?.name,
          productMeta?.sku,
          productMeta?.barcode,
        ]
          .filter((value) => value !== undefined && value !== null && String(value).trim() !== '')
          .join(' ')
          .toLowerCase();
        return haystack.includes(searchValue);
      });
    }

    return entries;
  }, [lineMetaEntries, lineFilter, lineSearch, resolveProductForLine]);

  const filteredCount = filteredLineEntries.length;

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(Math.max(filteredCount, 1) / LINES_PER_PAGE));
    setLinePage((prev) => Math.min(prev, totalPages));
  }, [filteredCount]);

  const totalLinePages = Math.max(1, Math.ceil(Math.max(filteredCount, 1) / LINES_PER_PAGE));
  const currentLinePage = Math.min(linePage, totalLinePages);
  const startIndex = (currentLinePage - 1) * LINES_PER_PAGE;
  const paginatedEntries = filteredLineEntries.slice(startIndex, startIndex + LINES_PER_PAGE);
  const hasLineFiltersApplied = lineFilter !== 'all' || lineSearch.trim() !== '';
  const renderPaginationControls = (extraClassName = '') => (
    <div
      className={`flex items-center gap-2 flex-wrap text-sm text-gray-600 justify-end ${extraClassName}`}
    >
      <button
        type="button"
        onClick={() => setLinePage(1)}
        disabled={currentLinePage === 1}
        className="px-3 py-1 border rounded disabled:opacity-50"
        aria-label="First page"
      >
        «
      </button>
      <button
        type="button"
        onClick={() => setLinePage((prev) => Math.max(prev - 1, 1))}
        disabled={currentLinePage === 1}
        className="px-3 py-1 border rounded disabled:opacity-50"
        aria-label="Previous page"
      >
        ‹
      </button>
      <span className="font-medium whitespace-nowrap">
        Page {currentLinePage} of {Math.max(totalLinePages, 1)}
      </span>
      <button
        type="button"
        onClick={() => setLinePage((prev) => Math.min(prev + 1, totalLinePages))}
        disabled={currentLinePage === totalLinePages}
        className="px-3 py-1 border rounded disabled:opacity-50"
        aria-label="Next page"
      >
        ›
      </button>
      <button
        type="button"
        onClick={() => setLinePage(totalLinePages)}
        disabled={currentLinePage === totalLinePages}
        className="px-3 py-1 border rounded disabled:opacity-50"
        aria-label="Last page"
      >
        »
      </button>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {isReadOnly && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-md p-3 text-sm">
          This purchase order is read-only.
        </div>
      )}
      {purchaseOrder && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <div>
            <p className="text-xs uppercase text-gray-500">Invoice Subtotal</p>
            <p className="text-lg font-semibold text-gray-900">${displaySummary.subtotal.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-gray-500">Tax ({displaySummary.taxRate}%)
            </p>
            <p className="text-lg font-semibold text-gray-900">${displaySummary.tax.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-gray-500">Lines</p>
            <p className="text-lg font-semibold text-gray-900">{displaySummary.lineCount}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-gray-500">Invoice Total</p>
            <p className="text-2xl font-bold text-gray-900">${displaySummary.grandTotal.toFixed(2)}</p>
          </div>
          <div>
            <p className='text-xs uppercase text-gray-500'>Approval Date</p>
            <p className='text-lg font-semibold text-gray-900'>{approvalDateDisplay}</p>
          </div>
          <div>
            <p className='text-xs uppercase text-gray-500'>Approver's Name</p>
            <p className='text-lg font-semibold text-gray-900'>{approverNameDisplay}</p>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Supplier</label>
          <select
            name="supplier"
            value={form.supplier}
            onChange={handleChange}
            required
            disabled={isReadOnly}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm disabled:bg-gray-100 disabled:text-gray-500"
          >
            <option value="">Select supplier</option>
            {suppliers.map((supplier) => (
              <option key={supplier._id} value={supplier._id}>
                {supplier.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Destination Store</label>
          <input type="hidden" name="destinationStore" value={form.destinationStore} />
          <input
            value={
              purchaseOrder?.destinationStore?.name ||
              defaultStoreName ||
              form.destinationStore
            }
            disabled
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-600"
          />
          <p className="text-xs text-gray-500 mt-1">
            Change the active store in the header to create orders for another location.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Invoice Number</label>
          <input
            name="invoiceNumber"
            value={form.invoiceNumber}
            onChange={handleChange}
            disabled={isReadOnly}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm disabled:bg-gray-100 disabled:text-gray-500"
            placeholder="Supplier invoice reference"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Invoice Date</label>
          <input
            type="date"
            name="invoiceDate"
            value={form.invoiceDate}
            onChange={handleChange}
            disabled={isReadOnly}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm disabled:bg-gray-100 disabled:text-gray-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Delivery Date</label>
          <input
            type="date"
            name="deliveryDate"
            value={form.deliveryDate}
            onChange={handleChange}
            disabled={isReadOnly}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm disabled:bg-gray-100 disabled:text-gray-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Due Date</label>
          <input
            type="date"
            name="expectedDate"
            value={form.expectedDate}
            onChange={handleChange}
            disabled={isReadOnly}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm disabled:bg-gray-100 disabled:text-gray-500"
          />
          <p className="text-xs text-gray-500 mt-1">Optional for drafts. Required before submitting the PO.</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Order Value</label>
          <div className="relative mt-1">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
              $
            </span>
            <input
              type="number"
              name="orderValue"
              min="0"
              step="0.01"
              value={form.orderValue}
              onChange={handleChange}
              disabled={isReadOnly}
              className="w-full rounded-md border-gray-300 pl-8 shadow-sm disabled:bg-gray-100 disabled:text-gray-500"
              placeholder="Optional when line items are not ready"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Used as total when there are no line items yet.</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Invoice Tax (%)</label>
          <input
            type="number"
            name="invoiceTaxRate"
            min="0"
            step="0.01"
            value={form.invoiceTaxRate}
            onChange={handleChange}
            disabled={isReadOnly}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm disabled:bg-gray-100 disabled:text-gray-500"
            placeholder="10"
          />
          <p className="text-xs text-gray-500 mt-1">Tax amount: ${invoiceTaxAmount.toFixed(2)}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Calculated Invoice Amount</label>
          <input
            type="text"
            value={`$${computedInvoiceAmount.toFixed(2)}`}
            readOnly
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-700"
          />
          <p className="text-xs text-gray-500 mt-1">Subtotal: ${invoiceSubtotal.toFixed(2)}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Supplier Invoice Amount</label>
          <div className="relative mt-1">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
              $
            </span>
            <input
              type="number"
              name="supplierInvoiceAmount"
              min="0"
              step="0.01"
              value={form.supplierInvoiceAmount}
              onChange={handleChange}
              disabled={isReadOnly}
              className="w-full rounded-md border-gray-300 pl-8 shadow-sm disabled:bg-gray-100 disabled:text-gray-500"
              placeholder="Invoice total from supplier"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Captured from the supplier invoice.</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Notes</label>
          <textarea
            name="notes"
            value={form.notes}
            onChange={handleChange}
            rows={3}
            disabled={isReadOnly}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm disabled:bg-gray-100 disabled:text-gray-500"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={saving} className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
          {isReadOnly ? 'Close' : 'Cancel'}
        </button>
        {!isReadOnly && (
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-md bg-green-600 text-white disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? 'Saving...' : purchaseOrder ? 'Update Order' : 'Create Order'}
          </button>
        )}
      </div>

      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-gray-700">Line Items</h3>
            <p className="text-xs text-gray-500">
              Import supplier CSVs or enter lines manually.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full sm:w-72">
              <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={lineSearch}
                onChange={(e) => {
                  setLineSearch(e.target.value);
                  setLinePage(1);
                }}
                placeholder="Search product, SKU, barcode"
                className="h-9 w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 text-sm text-gray-700 shadow-sm focus:border-primary focus:ring-primary"
              />
            </div>
            {!isReadOnly && (
              <>
                <label className="inline-flex h-9 cursor-pointer items-center rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50">
                  <FiUpload className="mr-2" /> Import CSV
                  <input type="file" accept=".csv,text/csv" onChange={handleImportCsv} className="hidden" />
                </label>
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  className="inline-flex h-9 items-center rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <FiDownload className="mr-2" /> CSV Template
                </button>
                <button
                  type="button"
                  onClick={addLine}
                  className="inline-flex h-9 items-center rounded-md bg-primary px-3 text-sm font-semibold text-white hover:bg-primary-dark"
                >
                  <FiPlusCircle className="mr-2" /> Add Line
                </button>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600 md:flex-row md:items-center md:justify-between">
          <div>
            Showing {paginatedEntries.length ? `${startIndex + 1}-${startIndex + paginatedEntries.length}` : '0'} of {filteredCount} line items
            {hasLineFiltersApplied && filteredCount !== form.lines.length && form.lines.length > 0 && (
              <span className="text-gray-400"> (filtered from {form.lines.length})</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="uppercase font-semibold tracking-wide text-gray-500">Review</span>
            <select
              value={lineFilter}
              onChange={(e) => {
                setLineFilter(e.target.value);
                setLinePage(1);
              }}
              className="rounded-md border-gray-300 text-sm shadow-sm"
            >
              <option value="all">All line items</option>
              <option value="cost">Cost differences only</option>
              <option value="price">Price differences only</option>
              <option value="any">Any differences (cost, price, barcode)</option>
            </select>
            <span className="flex items-center gap-1 text-amber-600">
              <span className="inline-block h-2 w-2 rounded-full bg-amber-500" />
              Highlighted rows show barcode mismatch and supplier cost/price variance.
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1180px] w-full border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-gray-100 text-[11px] uppercase tracking-[0.08em] text-gray-600">
              <tr>
                <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">Product</th>
                <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">SKU</th>
                <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">Barcode</th>
                <th className="border-b border-gray-200 px-3 py-2 text-center font-semibold">GST</th>
                <th className="border-b border-gray-200 px-3 py-2 text-right font-semibold">Qty</th>
                <th className="border-b border-gray-200 px-3 py-2 text-right font-semibold">Unit Cost</th>
                <th className="border-b border-gray-200 px-3 py-2 text-right font-semibold">Line Total</th>
                <th className="border-b border-gray-200 px-3 py-2 text-right font-semibold">Retail Price</th>
                {!isReadOnly && <th className="border-b border-gray-200 px-3 py-2 text-right font-semibold">Action</th>}
              </tr>
            </thead>
            <tbody>
              {!paginatedEntries.length && (
                <tr>
                  <td colSpan={isReadOnly ? 8 : 9} className="px-6 py-8 text-center text-gray-500">
                    {form.lines.length === 0 ? 'No line items added yet.' : 'No line items match the selected filter.'}
                  </td>
                </tr>
              )}
              {paginatedEntries.map(
                ({
                  line,
                  index: globalIndex,
                  hasCostDelta,
                  hasPriceDelta,
                  hasBarcodeMismatch,
                  lineBarcodeInput,
                  productBarcode,
                  baselineCost,
                  baselinePrice,
                  costDelta,
                  priceDelta,
                }, rowIndex) => {
                  const lineTotal = lineTotals[globalIndex] || 0;
                  const selectedProduct =
                    line.mode === 'existing'
                      ? resolveProductForLine(line)
                      : null;
                  const resolvedSku = line.mode === 'existing' ? (line.sku || selectedProduct?.sku || '') : line.sku || '';
                  const resolvedBarcode =
                    line.mode === 'existing'
                      ? selectedProduct?.barcode ?? line.barcode ?? ''
                      : line.barcode || '';
                  const hasVarianceHighlight = hasCostDelta || hasPriceDelta;
                  const highlightClass = hasBarcodeMismatch
                    ? 'bg-red-50'
                    : hasVarianceHighlight
                      ? 'bg-amber-50'
                      : rowIndex % 2 === 0
                        ? 'bg-white'
                        : 'bg-gray-50';
                  const costDeltaText = formatDelta(costDelta);
                  const priceDeltaText = formatDelta(priceDelta);
                  const showComparison = hasCostDelta || hasPriceDelta;
                  const showBarcodeMismatchNotice = hasBarcodeMismatch && line.mode === 'existing';
                  return (
                    <React.Fragment
                      key={line.clientLineId || `${line.product || line.name || 'line'}-${globalIndex}`}
                    >
                      <tr className={highlightClass}>
                        <td className="border-b border-gray-200 px-3 py-2 align-top">
                          {line.mode === 'existing' ? (
                            <div className="w-72 space-y-2">
                              <button
                                type="button"
                                onClick={() => {
                                  if (!isReadOnly) {
                                    openCatalogLookup(globalIndex);
                                  }
                                }}
                                className="flex min-h-[2.5rem] w-full items-start justify-between rounded-md border border-gray-200 bg-white px-3 py-2 text-left disabled:cursor-default disabled:border-transparent disabled:bg-transparent disabled:px-0"
                                disabled={isReadOnly}
                              >
                                <div className="min-w-0">
                                  <div className="truncate text-sm font-medium text-gray-900">
                                    {selectedProduct?.name || line.name || line.description || 'Select product'}
                                  </div>
                                  <div className="truncate text-xs text-gray-500">
                                    {formatProductSubtitle(selectedProduct) || 'Search by product, SKU, or barcode'}
                                  </div>
                                </div>
                                {!isReadOnly && (
                                  <span className="ml-3 shrink-0 text-xs font-medium text-blue-600">Change</span>
                                )}
                              </button>
                              {catalogLookup.lineIndex === globalIndex && !isReadOnly && (
                                <div className="rounded-md border border-gray-200 bg-white p-2 shadow-sm">
                                  <div className="relative">
                                    <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                      autoFocus
                                      type="text"
                                      value={catalogLookup.query}
                                      onChange={(e) =>
                                        setCatalogLookup((prev) => ({
                                          ...prev,
                                          query: e.target.value,
                                          error: '',
                                        }))
                                      }
                                      placeholder="Search product, SKU, barcode"
                                      className="h-9 w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 text-sm text-gray-700 shadow-sm focus:border-primary focus:ring-primary"
                                    />
                                  </div>
                                  {catalogLookup.error && (
                                    <p className="mt-2 text-xs text-red-600">{catalogLookup.error}</p>
                                  )}
                                  <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-gray-100">
                                    {catalogLookup.loading && (
                                      <div className="px-3 py-2 text-xs text-gray-500">Searching products...</div>
                                    )}
                                    {!catalogLookup.loading &&
                                      catalogLookup.results.map((product) => (
                                        <button
                                          key={product._id}
                                          type="button"
                                          onClick={() => selectCatalogProduct(globalIndex, product)}
                                          className="w-full border-b border-gray-100 px-3 py-2 text-left last:border-b-0 hover:bg-slate-50"
                                        >
                                          <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                          <div className="text-xs text-gray-500">
                                            {formatProductSubtitle(product) || 'Catalog product'}
                                          </div>
                                        </button>
                                      ))}
                                    {!catalogLookup.loading && catalogLookup.query.trim() && !catalogLookup.results.length && (
                                      <div className="px-3 py-2 text-xs text-gray-500">No products found.</div>
                                    )}
                                    {!catalogLookup.loading && !catalogLookup.query.trim() && !catalogLookup.results.length && (
                                      <div className="px-3 py-2 text-xs text-gray-500">
                                        Start typing to search the catalog.
                                      </div>
                                    )}
                                  </div>
                                  <div className="mt-2 flex items-center justify-between">
                                    <button
                                      type="button"
                                      className="text-xs font-medium text-gray-500 hover:text-gray-700"
                                      onClick={closeCatalogLookup}
                                    >
                                      Done
                                    </button>
                                    <button
                                      type="button"
                                      className="text-xs font-medium text-blue-600 hover:text-blue-700"
                                      onClick={() => switchLineMode(globalIndex, 'new')}
                                    >
                                      New product
                                    </button>
                                  </div>
                                </div>
                              )}
                              {!isReadOnly && (
                                <p className="text-xs text-gray-500">Catalog products are searched on demand.</p>
                              )}
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <input
                                value={line.name}
                                onChange={(e) => updateLine(globalIndex, 'name', e.target.value)}
                                className="h-8 w-56 rounded-md border border-gray-200 bg-white px-2 text-sm disabled:border-transparent disabled:bg-transparent disabled:px-0"
                                disabled={isReadOnly}
                                required
                              />
                              {!isReadOnly && (
                                <button
                                  type="button"
                                  className="text-xs font-medium text-blue-600 hover:text-blue-700"
                                  onClick={() => switchLineMode(globalIndex, 'existing')}
                                >
                                  Use catalog product
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="border-b border-gray-200 px-3 py-2 align-top">
                          <input
                            value={resolvedSku}
                            onChange={(e) => {
                              if (line.mode === 'new') updateLine(globalIndex, 'sku', e.target.value);
                            }}
                            readOnly={line.mode === 'existing' || isReadOnly}
                            className="h-8 w-28 rounded-md border border-gray-200 bg-white px-2 text-sm read-only:border-transparent read-only:bg-transparent read-only:px-0"
                          />
                        </td>
                        <td className="border-b border-gray-200 px-3 py-2 align-top">
                          <input
                            value={resolvedBarcode}
                            onChange={(e) => {
                              if (line.mode === 'new') updateLine(globalIndex, 'barcode', e.target.value);
                            }}
                            readOnly={line.mode === 'existing' || isReadOnly}
                            className="h-8 w-36 rounded-md border border-gray-200 bg-white px-2 text-sm read-only:border-transparent read-only:bg-transparent read-only:px-0"
                          />
                        </td>
                        <td className="border-b border-gray-200 px-3 py-2 text-center align-top">
                          <select
                            value={line.gstApplicable === false ? 'false' : 'true'}
                            onChange={(e) => updateLine(globalIndex, 'gstApplicable', e.target.value === 'true')}
                            className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm disabled:border-transparent disabled:bg-transparent disabled:px-0"
                            disabled={isReadOnly}
                          >
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </select>
                        </td>
                        <td className="border-b border-gray-200 px-3 py-2 text-right align-top">
                          <input
                            type="number"
                            min="0"
                            value={line.quantity}
                            onChange={(e) => updateLine(globalIndex, 'quantity', Number(e.target.value))}
                            className="h-8 w-20 rounded-md border border-gray-200 bg-white px-2 text-right text-sm disabled:border-transparent disabled:bg-transparent disabled:px-0"
                            required
                            disabled={isReadOnly}
                          />
                        </td>
                        <td className="border-b border-gray-200 px-3 py-2 text-right align-top">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={line.unitCost}
                            onChange={(e) => updateLine(globalIndex, 'unitCost', e.target.value)}
                            className="h-8 w-24 rounded-md border border-gray-200 bg-white px-2 text-right text-sm disabled:border-transparent disabled:bg-transparent disabled:px-0"
                            disabled={isReadOnly}
                          />
                        </td>
                        <td className="border-b border-gray-200 px-3 py-2 text-right font-medium text-gray-700 align-top">
                          ${lineTotal.toFixed(2)}
                        </td>
                        <td className="border-b border-gray-200 px-3 py-2 text-right align-top">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={line.price ?? ''}
                            onChange={(e) => updateLine(globalIndex, 'price', e.target.value)}
                            className="h-8 w-24 rounded-md border border-gray-200 bg-white px-2 text-right text-sm disabled:border-transparent disabled:bg-transparent disabled:px-0"
                            disabled={isReadOnly}
                          />
                        </td>
                        {!isReadOnly && (
                          <td className="border-b border-gray-200 px-3 py-2 text-right align-top">
                            <button
                              type="button"
                              aria-label={`Remove line ${globalIndex + 1}`}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-red-100 text-red-600 hover:bg-red-50 hover:text-red-800"
                              onClick={() => removeLine(globalIndex)}
                            >
                              <FiTrash2 />
                            </button>
                          </td>
                        )}
                      </tr>
                      {(showBarcodeMismatchNotice || showComparison) && (
                        <tr className={highlightClass}>
                          <td colSpan={isReadOnly ? 8 : 9} className="border-b border-gray-200 px-3 pb-2 pt-0">
                            <div className="space-y-2 text-xs">
                              {showBarcodeMismatchNotice && (
                                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-800">
                                  Barcode mismatch: line barcode {lineBarcodeInput} differs from product barcode {productBarcode}. Saving this purchase order will use the product barcode.
                                </div>
                              )}
                              {showComparison && (
                                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                                  Cost {baselineCost !== undefined ? `$${formatCurrency(baselineCost)}` : 'N/A'}
                                  {costDeltaText ? ` (${costDeltaText})` : ''} · Price {baselinePrice !== undefined ? `$${formatCurrency(baselinePrice)}` : 'N/A'}
                                  {priceDeltaText ? ` (${priceDeltaText})` : ''}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                }
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3">
          {renderPaginationControls()}
        </div>
      </section>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={saving} className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
          {isReadOnly ? 'Close' : 'Cancel'}
        </button>
        {!isReadOnly && (
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-md bg-green-600 text-white disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? 'Saving...' : purchaseOrder ? 'Update Order' : 'Create Order'}
          </button>
        )}
      </div>
    </form>
  );
};

export default PurchaseOrderForm;
